import { FollowUpType } from "models/appointment";
import { ScreeningFlow } from "models/screening-flow";
import { ScreeningPaymentType } from "pages/home/screening/payment-types";
import { IAnalyticsService } from ".";
import { getScreeningFlow } from "../session-storage";
import { OpenModalTrigger, UserActionEvent } from "./events";
import { LabType } from "../lab-type-helper";

export type BookingFlowLabTypeLabel = "public" | "private";

type BookingFlowEventProperties = Record<
    string,
    string | number | boolean | undefined
>;

function withScreeningType(
    properties: BookingFlowEventProperties = {},
    screeningFlow?: ScreeningFlow
): BookingFlowEventProperties {
    return {
        ...properties,
        screeningType: screeningFlow ?? getScreeningFlow(),
    };
}

function track(
    analytics: IAnalyticsService | null,
    event: UserActionEvent,
    properties: BookingFlowEventProperties = {},
    screeningFlow?: ScreeningFlow
): void {
    analytics
        ?.cdp()
        ?.trackEvent(event, withScreeningType(properties, screeningFlow));
}

function trackCdpAndWeb(
    analytics: IAnalyticsService | null,
    event: UserActionEvent,
    properties: BookingFlowEventProperties = {},
    screeningFlow?: ScreeningFlow
): void {
    const eventProperties = withScreeningType(properties, screeningFlow);
    analytics?.cdp()?.trackEvent(event, eventProperties);
    analytics?.web()?.trackEvent(event, eventProperties);
}

export function trackBookingFlowInitiated(
    analytics: IAnalyticsService | null,
    screeningFlow: ScreeningFlow
): void {
    track(analytics, UserActionEvent.BookingFlowInitiated, {}, screeningFlow);
}

export function trackBookingFlowLabSelected(
    analytics: IAnalyticsService | null,
    lab: BookingFlowLabTypeLabel,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLabSelected,
        { lab },
        screeningFlow
    );
}

export function trackBookingFlowConsultationSelected(
    analytics: IAnalyticsService | null,
    followUpType: FollowUpType,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowConsultationSelected,
        { consultation: followUpType },
        screeningFlow
    );
}

export function trackBookingFlowClinicSelected(
    analytics: IAnalyticsService | null,
    appointmentClinicId: number,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowClinicSelected,
        { appointmentClinicId },
        screeningFlow
    );
}

export function trackBookingFlowClinicListViewed(
    analytics: IAnalyticsService | null,
    location: string,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowClinicListViewed,
        { location },
        screeningFlow
    );
}

export function trackBookingFlowClinicListFilterChanged(
    analytics: IAnalyticsService | null,
    city: string,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowClinicListFilterChanged,
        { city },
        screeningFlow
    );
}

export function trackBookingFlowPersonalFormFilled(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowPersonalFormFilled,
        {},
        screeningFlow
    );
}

export function trackBookingFlowPrivacyConsentAccepted(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowPrivacyConsentAccepted,
        {},
        screeningFlow
    );
}

export function trackBookingFlowPrivacyConsentDeclined(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowPrivacyConsentDeclined,
        {},
        screeningFlow
    );
}

export function trackBookingFlowMedicalEvaluationSubmitted(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowMedicalEvaluationSubmitted,
        {},
        screeningFlow
    );
}

export function trackBookingFlowRecommendedTestsSelected(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowRecommendedTestsSelected,
        {},
        screeningFlow
    );
}

function isExpressWalletMethod(method: string): boolean {
    return (
        method === ScreeningPaymentType.ApplePay ||
        method === ScreeningPaymentType.GooglePay
    );
}

export function trackBookingFlowPaymentCompleted(
    analytics: IAnalyticsService | null,
    method: string,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowPaymentCompleted,
        {
            method,
            expressPay: isExpressWalletMethod(method),
        },
        screeningFlow
    );
}

export type BookingFlowAppointmentBookedProperties = {
    screeningFlow?: ScreeningFlow;
    appointmentCount: number;
    lab: BookingFlowLabTypeLabel;
    consultation: FollowUpType;
};

export function trackBookingFlowAppointmentBooked(
    analytics: IAnalyticsService | null,
    {
        screeningFlow,
        appointmentCount,
        lab,
        consultation,
    }: BookingFlowAppointmentBookedProperties
): void {
    trackCdpAndWeb(
        analytics,
        UserActionEvent.BookingFlowAppointmentBooked,
        {
            appointmentCount,
            lab,
            consultation,
        },
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupViewed(
    analytics: IAnalyticsService | null,
    trigger: OpenModalTrigger,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupViewed,
        { trigger },
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupSubmitted(
    analytics: IAnalyticsService | null,
    properties: {
        method?: "postal code" | "geolocation";
        lab?: LabType;
    },
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupSubmitted,
        properties,
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupLocationChanged(
    analytics: IAnalyticsService | null,
    selection: "postal code" | "geolocation",
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupLocationChanged,
        { selection },
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupClosed(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupClosed,
        {},
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupLabChanged(
    analytics: IAnalyticsService | null,
    selection: LabType,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupLabChanged,
        { selection },
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupToggleClicked(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupToggleClicked,
        {},
        screeningFlow
    );
}

export function trackBookingFlowLocationPopupPopoverClicked(
    analytics: IAnalyticsService | null,
    screeningFlow?: ScreeningFlow
): void {
    track(
        analytics,
        UserActionEvent.BookingFlowLocationPopupPopoverClicked,
        {},
        screeningFlow
    );
}

/** Legacy screening pay event — use for non–booking-flow-redesign payment UIs. */
export function trackScreeningPaymentSubmitted(
    analytics: IAnalyticsService | null,
    method: string
): void {
    analytics?.cdp()?.trackEvent(UserActionEvent.ScreeningPaymentSubmitted, {
        method,
    });
}

/** Redesign pay step — booking-flow funnel only (no legacy screening payment event). */
export function trackBookingFlowPayment(
    analytics: IAnalyticsService | null,
    method: string,
    screeningFlow?: ScreeningFlow
): void {
    trackBookingFlowPaymentCompleted(analytics, method, screeningFlow);
}
